/**
 * Frontend mirror of the renter notification feed (api `RenterNotificationDto`,
 * rental-clock v2.1 §5). The renter's cabinet channel: the platform crons and the FSM
 * write one row per event and the same row also drives a best-effort email.
 *
 * Telegram for renters is deliberately not here — that leg needs a dedicated bot and was
 * postponed (spec §5 header, decision of 2026-08-22): the cabinet is the channel.
 */

export enum RenterNotificationType {
  RENTAL_STARTED = 'rental.started',
  RENTAL_ENDING_24H = 'rental.ending-24h',
  RENTAL_OVERDUE_CHARGED = 'rental.overdue-charged',
  RENTAL_DEPOSIT_EXHAUSTED = 'rental.deposit-exhausted',
  RENTAL_DEPOSIT_RELEASED = 'rental.deposit-released',
  RENTAL_BOOKING_EXPIRED = 'rental.booking-expired',
}

export enum RenterNotificationStream {
  RENTAL = 'rental',
}

/**
 * Display snapshot written with the event. Every field is optional on purpose: it is a
 * snapshot of whatever the writer knew at the time, and the copy degrades rather than
 * throws when a field is missing.
 */
export interface ApiRenterNotificationPayload {
  make?: string;
  model?: string;
  startDate?: string;
  endDate?: string;
  currency?: string;
  /** Overdue day number this charge was for. */
  day?: number;
  /** Amount charged for the day(s) in this event, display units. */
  chargedAmount?: number;
  /** Deposit left after the charge, display units. */
  remainingDeposit?: number;
  /** Deposit held / released, display units. */
  depositAmount?: number;
  amount?: number;
  /** The 1–3 day rate charged per started overdue day, display units. */
  dailyOverdueRate?: number;
  [key: string]: unknown;
}

export interface ApiRenterNotification {
  id: string;
  accountId: string;
  type: RenterNotificationType;
  stream: RenterNotificationStream;
  /** Source entity — the rental deal the event belongs to. */
  entityId: string;
  payload: ApiRenterNotificationPayload | null;
  createdAt: string;
  readAt: string | null;
}

export interface ApiRenterNotificationList {
  items: ApiRenterNotification[];
  total: number;
  /** Unread notifications of the account (bell badge). */
  unreadCount: number;
}
